import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppTextEditor from '@nxsys/components/AppTextEditor';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { ContentState, EditorState } from 'draft-js';
import React, {
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import htmlToDraft from 'html-to-draftjs';
import { Option } from '@nxsys/types/models/Common';
import { defaultStatusOptions } from '@nxsys/constants/AppConstants';
import { IEmailTemplate } from '@nxsys/types/models/emailTemplate';

const normalizeEditorHtml = (value: string) =>
  value && value !== '<p></p>'
    ? value.replace(/<p><\/p>/g, '<br></br>')
    : value;

export type EmailTemplateFormValues = {
  templateId: string;
  status: Option<string> | null;
  subject: string;
  context: string;
  signature: string;
};

export type EmailTemplateFormRef = {
  submit: () => void;
};

type EmailTemplateFormProps = {
  formRef?: Ref<EmailTemplateFormRef>;
  onSubmit: (params: EmailTemplateFormValues) => Promise<void>;
  data?: IEmailTemplate;
};

const EmailTemplateForm: React.FC<EmailTemplateFormProps> = ({
  formRef,
  onSubmit,
  data,
}) => {
  const { messages } = useTypedIntl();
  const [needReinitializeForm, setNeedReinitializeForm] = useState(true);

  const [form] = Form.useForm();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  useEffect(() => {
    if (data && needReinitializeForm) {
      form.setFieldsValue({
        templateId: data.templateId || '',
        status:
          defaultStatusOptions?.find((item) => item.value === data.status) ||
          null,
        subject: data.subject || '',
        context: data.context || '',
        signature: data.signature || '',
      });
      setNeedReinitializeForm(false);
    }
  }, [data]);

  const onFinish: FormProps['onFinish'] = async (values) => {
    onSubmit(values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log(errorInfo);
  };

  const contextEditorStateInit = useMemo(() => {
    const content = data?.context || '';
    if (!content) return EditorState.createEmpty();

    try {
      const blocksFromHtml = htmlToDraft(content);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap,
      );
      return EditorState.createWithContent(contentState);
    } catch (error) {
      return EditorState.createEmpty();
    }
  }, [data?.context]);

  const signatureEditorStateInit = useMemo(() => {
    const content = data?.signature || '';
    if (!content) return EditorState.createEmpty();

    try {
      const blocksFromHtml = htmlToDraft(content);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap,
      );
      return EditorState.createWithContent(contentState);
    } catch (error) {
      return EditorState.createEmpty();
    }
  }, [data?.signature]);

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <Form.Item
        label={messages['company.emailTemplate.templateId']}
        name="templateId"
      >
        <AppInput
          disabled
          placeholder={messages['company.emailTemplate.templateId']}
        />
      </Form.Item>

      <Form.Item
        label={messages['company.emailTemplate.templateStatus']}
        name="status"
      >
        <AppSelect
          placeholder={messages['form.select']}
          options={defaultStatusOptions}
        />
      </Form.Item>

      <Form.Item
        label={messages['company.emailTemplate.subject']}
        name="subject"
      >
        <AppInput placeholder={messages['form.enter']} />
      </Form.Item>

      <Form.Item
        label={messages['company.emailTemplate.context']}
        name="context"
      >
        <AppTextEditor
          isSimple
          placeholder={messages['form.enter']}
          editorStateInit={contextEditorStateInit}
          handleChange={(htmlContent) => {
            form.setFieldsValue({ context: normalizeEditorHtml(htmlContent) });
          }}
        />
      </Form.Item>

      <Form.Item
        label={messages['company.emailTemplate.signature']}
        name="signature"
      >
        <AppTextEditor
          isSimple
          placeholder={messages['form.enter']}
          editorStateInit={signatureEditorStateInit}
          handleChange={(htmlContent) => {
            form.setFieldsValue({
              signature: normalizeEditorHtml(htmlContent),
            });
          }}
        />
      </Form.Item>
    </AppFormContainer>
  );
};

export default EmailTemplateForm;
